// ============================================================================
// src/lib/layerOperationsApi.ts — the one client of public.layer_apply_operation
// ----------------------------------------------------------------------------
// Migration 242 (R4) replaces the layer batch and housing pages' whole-row
// upserts and best-effort client Activity with ONE audited server entrypoint:
// a narrow change to one record under its row lock, checked against the
// caller's expected-before values, with the Activity rows and an immutable
// request receipt written in the same transaction.
//
// The transport and reply semantics are pigOperationsApi's, deliberately
// identical (that module is the reviewed contract for 231/235):
//   * ONE request id per user action (`newLayerRequestId`); a retry of the same
//     action reuses it, so the server replays rather than re-applies;
//   * a transport failure is retried here with the SAME id; a database refusal
//     (any SQLSTATE) is an answer and is never retried;
//   * a replay carries no rows and an `unknown` outcome tells the caller to
//     re-read before it adopts anything;
//   * expected values come off the PERSISTED row (`layerExpected`), never off a
//     form.
// It is a separate module rather than a generalised pigOperationsApi so the
// reviewed pig transport stays byte-for-byte as reviewed.
//
// THE HEN-COUNT RULE (242's anchor): a change to `current_count` must name the
// expected `current_count` AND `current_count_date`; `layerExpected` adds the
// date whenever the count is among the changed fields.
// ============================================================================

import type {SupabaseClient} from './supabaseTypes.js';
import {newClientSubmissionId} from './clientSubmissionId.js';

export type LayerOperation =
  | 'batch.create'
  | 'batch.update'
  | 'batch.feed_rates'
  | 'housing.create'
  | 'housing.update'
  | 'retirement_home.update';

export type LayerOpTarget = {batch_id?: string; housing_id?: string};

type Json = unknown;
export type LayerConflict = {field: string; expected: Json; current: Json};

export type LayerOpRequest = {
  requestId: string;
  operation: LayerOperation;
  target: LayerOpTarget;
  expected?: Record<string, Json>;
  change?: Record<string, Json>;
};

export type LayerOpOutcome =
  | {
      status: 'saved';
      kind: 'created' | 'updated';
      written: Record<string, Json>;
      eventIds: string[];
      rows: Record<string, Json> | null;
      replayed: boolean;
      mustReread: boolean;
    }
  | {status: 'no_change'; rows: Record<string, Json> | null}
  | {status: 'conflict'; conflicts: LayerConflict[]; rows: Record<string, Json> | null}
  | {status: 'refused'; reason: string; rows: Record<string, Json> | null}
  | {status: 'rejected'; code: string; message: string}
  | {status: 'unknown'; message: string; mustReread: true};

/** A fresh id for ONE user action. Keep it with the draft and reuse it on retry. */
export function newLayerRequestId(): string {
  return 'layer-' + newClientSubmissionId();
}

/** Whether a stored value already means the form's value (null and '' are both "empty"). */
export function sameLayerValue(stored: unknown, next: unknown): boolean {
  const a = stored === undefined || stored === '' ? null : stored;
  const b = next === undefined || next === '' ? null : next;
  if (typeof a === 'number' || typeof b === 'number') return a !== null && b !== null && Number(a) === Number(b);
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The fields of `allowed` whose `next` value differs from the persisted `row`,
 * and the expected/change pair for exactly those. A changed `current_count`
 * also expects the stored `current_count_date` (242's anchor rule).
 */
export function layerPatch(
  row: Record<string, Json> | null | undefined,
  next: Record<string, Json>,
  allowed: readonly string[],
): {fields: string[]; expected: Record<string, Json>; change: Record<string, Json>} {
  const fields = allowed.filter(
    (f) => Object.prototype.hasOwnProperty.call(next, f) && !sameLayerValue(row?.[f], next[f]),
  );
  const expected: Record<string, Json> = {};
  const change: Record<string, Json> = {};
  for (const f of fields) {
    expected[f] = row?.[f] ?? null;
    change[f] = next[f] === '' ? null : (next[f] ?? null);
  }
  if (fields.includes('current_count')) expected.current_count_date = row?.current_count_date ?? null;
  return {fields, expected, change};
}

const ATTEMPTS = 3;
const ATTEMPT_TIMEOUT_MS = 8000;
const SQLSTATE = /^[0-9A-Z]{5}$/;
type RpcError = {code?: string; message?: string} | null;

function rowsOf(data: Record<string, Json>): Record<string, Json> | null {
  const rows = data.rows;
  return rows && typeof rows === 'object' && !Array.isArray(rows) ? (rows as Record<string, Json>) : null;
}

/** Read one reply. Anything that does not answer THIS request is not a receipt. */
export function interpretLayerReply(request: LayerOpRequest, data: unknown): LayerOpOutcome {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, Json>;
  if (d.request_id !== request.requestId || d.operation !== request.operation) {
    return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
  }
  const rows = rowsOf(d);
  switch (d.kind) {
    case 'created':
    case 'updated': {
      if (d.ok !== true) break;
      const replayed = d.replayed === true;
      return {
        status: 'saved',
        kind: d.kind,
        written: (d.written && typeof d.written === 'object' ? d.written : {}) as Record<string, Json>,
        eventIds: Array.isArray(d.event_ids) ? (d.event_ids as string[]) : [],
        rows: replayed ? null : rows,
        replayed,
        mustReread: replayed || rows === null,
      };
    }
    case 'no_change':
      return {status: 'no_change', rows};
    case 'conflict':
      return {status: 'conflict', conflicts: Array.isArray(d.conflicts) ? (d.conflicts as LayerConflict[]) : [], rows};
    case 'refused':
      return {status: 'refused', reason: typeof d.reason === 'string' ? d.reason : 'refused', rows};
  }
  return {status: 'unknown', message: 'Could not confirm this change was saved. Reload to check.', mustReread: true};
}

/**
 * Apply one layer operation. Never throws. Transport failures are retried with
 * the same request id; a database refusal comes back as `rejected` at once.
 */
export async function applyLayerOperation(sb: SupabaseClient, request: LayerOpRequest): Promise<LayerOpOutcome> {
  let lastMessage = 'No reply from the server.';
  for (let attempt = 1; attempt <= ATTEMPTS; attempt++) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    try {
      const call = sb.rpc('layer_apply_operation', {
        p_request_id: request.requestId,
        p_operation: request.operation,
        p_target: request.target,
        p_expected: request.expected ?? {},
        p_change: request.change ?? {},
      });
      const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ATTEMPT_TIMEOUT_MS);
      });
      const {data, error} = (await Promise.race([call, timeout])) as {data: unknown; error: RpcError};
      if (error) {
        if (error.code && SQLSTATE.test(error.code)) {
          return {status: 'rejected', code: error.code, message: error.message || 'The change was refused.'};
        }
        throw new Error(error.message || 'transport');
      }
      return interpretLayerReply(request, data);
    } catch (e) {
      lastMessage = (e as {message?: string}).message || lastMessage;
      if (attempt < ATTEMPTS) await new Promise((r) => setTimeout(r, 1000 * attempt));
    } finally {
      if (timer !== null) clearTimeout(timer);
    }
  }
  console.warn('layer_apply_operation: no confirmed reply after retries:', lastMessage);
  return {status: 'unknown', message: 'Could not reach the server. Your change is kept; retry.', mustReread: true};
}

/** The sentence for a refusal the database gave (a SQLSTATE), for an inline notice. */
export function layerRejectionMessage(outcome: Extract<LayerOpOutcome, {status: 'rejected'}>): string {
  switch (outcome.code) {
    case '42501':
      return 'You do not have permission to make this change.';
    case 'P0002':
      return 'This record no longer exists. Reload to see the current list.';
    case 'WCF01':
      return 'This change was already used for different saved work. Reload and try again.';
    case '22007':
    case '22008':
      return 'A date is not valid. Use the date picker.';
    // PROD's layer_batches constraints (242 v2 PROD-shape proof): cost_per_bird
    // is numeric(8,2), and batch names are UNIQUE.
    case '22003':
      return 'A number is too large for this field.';
    case '23505':
      return 'That name is already used by another batch.';
    default:
      return outcome.message.replace(/^layer_apply_operation:\s*/, '');
  }
}

/** One line per conflicting field, in the conflict notice's words. */
export function layerConflictMessage(conflicts: LayerConflict[], labels: Record<string, string>): string {
  const shown = (v: Json) => (v === null || v === undefined || v === '' ? '(empty)' : String(v));
  return [
    'Not saved: this changed while you were editing.',
    ...conflicts.map((c) => `${labels[c.field] || c.field} is now ${shown(c.current)} (someone else changed it).`),
  ].join('\n');
}
